// Substantive pipeline views (Ken 2026-09-02).
//
// TRUE execution order on a SLOT (→) × LANE (↓) ruler, ONE scoped story.
//   • STEP      — full box, long bold name.
//   • MINI-STEP — TYPE label above (FILTER / TIE-BREAK) + short bold name + inside
//                 `purpose` + caption below; detail lives in a gutter canvas.
//   • MANUAL    — dotted box: a human step, not automated.
//   • OUTPUTS below (right edge) · INPUTS above (`inputAlign`) · non-adjacent input
//     carries a source-slot superscript · GUIDES in the top notes lane.

export type FlowScope =
  | 'per-email' | 'per-issue' | 'per-issue-x-matter' | 'per-new-matter' | 'per-existing-matter'

export type FlowKind = 'llm' | 'code'
export type FlowLane = 'notes' | 'existing' | 'main' | 'new' | 'reserved'

export const LANES: { id: FlowLane; label: string }[] = [
  { id: 'notes', label: '' },
  { id: 'existing', label: '' },
  { id: 'main', label: '' },
  { id: 'new', label: '' },
  { id: 'reserved', label: '' },
]

export type FlowInput = { v: string; sup?: string }

export type FlowNode = {
  key: string
  name: string
  scope?: FlowScope
  kind: FlowKind
  lane: FlowLane
  order?: number
  fork?: boolean
  demoted?: boolean
  manual?: boolean             // human step — dotted box
  mini?: boolean
  typeLabel?: string
  purpose?: string
  inputAlign?: 'left' | 'right'
  outputs?: string[]
  inputs?: FlowInput[]
  guide?: string
  note?: string
}

export type IdfDetail = {
  title: string
  steps: string[]
  tagline: string
  outcomes?: { cond: string; result: string }[]
}

export type DecideExample = {
  title: string
  rule: string[]
  modifiers?: { label: string; text: string }[] // deterministic overrides between eligibility & selection
  rows: { m: string; s: string; tag: string }[]
  idf: string
  verdict: string
}

export type AExample = {
  title: string
  buckets: string[]
  result: string
  note?: string
}

export type SubstantiveStory = {
  id: string
  title: string
  question: string
  nodes: FlowNode[]
  aExample?: AExample
  aIdfDetail?: IdfDetail
  decideExample?: DecideExample
  idfDetail?: IdfDetail
}

// ── substantive-1 ────────────────────────────────────────────────────────────
export const FILING_STORY: SubstantiveStory = {
  id: 'substantive-1',
  title: 'substantive-1 · Filing',
  question: 'How do we file every email — match it to an existing matter or instantiate a new one?',
  nodes: [
    { key: 'dedup', name: 'Dedup Across Mailboxes', scope: 'per-email', kind: 'code', lane: 'main', order: 0,
      note: 'Same email in both mailboxes → keep one, ignore the copy (matched by RFC-822 Message-ID). File once.' },
    { key: 'is_personal', name: 'Classify Email for Basic Filtering', scope: 'per-email', kind: 'llm', lane: 'main', order: 1,
      note: 'Filters out personal / digest email; only substantive email continues.' },
    { key: 'email_structural', name: 'Split Email into New Message and Background', scope: 'per-email', kind: 'llm', lane: 'main', order: 2,
      note: 'Separates the fresh message from quoted history so issue-detection reads clean.' },
    { key: 'detect_issues', name: 'Detect "Issues" in Email', scope: 'per-email', kind: 'llm', lane: 'main', order: 3,
      inputs: [{ v: '{{new_email_message}}' }, { v: '{{email_background}}' }], inputAlign: 'right',
      outputs: ['{{email_issue_description}}'],
      note: 'Creates the Issue rows — the Issue, not the email, is the unit we file.' },
    { key: 'limit_candidates', name: 'Limit Candidate Matters', kind: 'code', lane: 'main', order: 4, mini: true, typeLabel: 'FILTER',
      purpose: 'Prefilters the candidate matters the detected issue will be compared against.',
      note: 'keeps only recent ∪ hot ∪ top-IDF matters' },
    { key: 'assess_matter_match', name: 'Fan-Out: Evaluate Matches between Issue and Existing Matters', scope: 'per-issue-x-matter', kind: 'llm', lane: 'main', order: 5,
      inputs: [{ v: '{{matter_description}}' }, { v: '{{matter_issue_history}}' }],
      outputs: ['{{key_entities_in_issue}}', 'confidence', '{{topical_overlap}}', '{{identity_certainty}}'],
      note: 'Per (issue × candidate matter): extracts key entities and scores the match.' },
    { key: 'aggregate_match_and_decide', name: 'Decide to File with Existing Matter or Instantiate New Matter', scope: 'per-issue', kind: 'code', lane: 'main', order: 6, fork: true,
      outputs: ['{{matter_id}}'],
      guide: 'filing_decision',
      note: 'THE filing decision (deterministic): pick the best eligible matter, or create a new one.' },
    { key: 'idf_distinctiveness', name: 'Distinctiveness Check', kind: 'code', lane: 'main', order: 7, mini: true, typeLabel: 'TIE-BREAK',
      purpose: 'For a middling match, decides it by how distinctive (rare) the shared entities are — a real match survives, a coincidence is dropped.',
      note: 'runs only for middle-band matches — detail below' },
    // branch UP: file into existing
    { key: 'revise_matter_name', name: 'Review Description of Existing Matter', scope: 'per-existing-matter', kind: 'llm', lane: 'existing', order: 8,
      inputs: [{ v: '{{matter_id}}', sup: '6' }, { v: '{{email_issue_description}}', sup: '3' }],
      outputs: ['{{matter_description}}'],
      note: 'Updates the matter’s description/keywords to absorb the new issue.' },
    { key: 'review_matter_name', name: 'Review Name of Existing Matter', scope: 'per-existing-matter', kind: 'llm', lane: 'existing', order: 9,
      outputs: ['{{matter_name}}'],
      note: 'Re-checks the matter name (only if the description changed).' },
    // branch DOWN: create new
    { key: 'update_matter_summary', name: 'Describe Matter Newly Instantiated', scope: 'per-new-matter', kind: 'llm', lane: 'new', order: 8,
      inputs: [{ v: '{{email_issue_description}}', sup: '3' }],
      outputs: ['{{matter_description}}'],
      note: 'Writes the new matter’s description/type from the issue.' },
    { key: 'name_new_matter', name: 'Name Matter Newly Instantiated', scope: 'per-new-matter', kind: 'llm', lane: 'new', order: 9,
      outputs: ['{{matter_name}}'],
      note: 'Names the new matter from its fresh description.' },
    // (Propose Folder Candidate + Decide File Path removed — folder-filing lives in its own diagram.)
    // human correction at the very end (dotted)
    { key: 'manual_refile', name: 'Manual Re-Filing', kind: 'code', lane: 'main', order: 10, manual: true,
      note: 'human: a reviewer can move an issue to the correct matter (which name-locks it).' },
    // off-grid dead-end: accounting, right after Detect Issues
    { key: 'route_accounting_issue', name: 'Route Accounting Issue', scope: 'per-issue', kind: 'code', lane: 'reserved', order: 3.5, demoted: true,
      guide: 'accounting_related',
      note: 'Accounting issues (invoices, payments) leave the filing pipeline for the Accounting Page — a dead-end.' },
  ],
  aExample: {
    title: 'A · FILTER — worked example: the Blockson issue vs the open-matter book',
    buckets: [
      'recent — the ~25 matters touched most recently',
      'hot — any matter flagged high-activity (always kept)',
      'top-IDF — the ~15 matters whose text most distinctively shares the issue’s significant tokens',
    ],
    result: 'union → 48 candidate matters go to Fan-Out; the rest are never LLM-scored.',
  },
  aIdfDetail: {
    title: 'A · top-IDF ranking (the IDF bucket of the FILTER)',
    steps: [
      'issue description → significant tokens',
      'each open matter: idf.max over shared tokens   (same idf = log(N/(1+df)) as C)',
      'rank matters by idf.max',
      'keep the top ~15 (FANOUT_IDF_K)',
    ],
    tagline: 'The cheap, pre-LLM cousin of C: it picks WHO gets scored, not WHO wins. Uses issue-description tokens because the LLM key-entities don’t exist until Fan-Out.',
  },
  decideExample: {
    title: 'B · DECIDE — worked example: issue EMAL-012141 “Blockson v Moravian”, 48 candidates',
    rule: [
      'STRONG:  conf ≥ 0.72  or  identity ≥ 0.70',
      'MIDDLE:  conf ≥ 0.55  and  topical ≥ 0.60  and  identity ≥ 0.45  and  distinctive → C',
      'select:  argmax( conf , idf.sum ) → file    else → create new',
    ],
    modifiers: [
      { label: 'Subject veto (−)', text: 'Title-IX ↔ Title-IX only: if both name a case subject and they are DISJOINT (shared cast/system but different complainant) → annul the eligible candidate.' },
      { label: 'Operator Lexicon (+)', text: 'A curated entity that ties to a matter → rescue a below-threshold candidate to eligible; an explicit “M-###” hint also lifts its confidence to the strong floor (wins selection).' },
    ],
    rows: [
      { m: 'M-020 “Blockson, Laquita”', s: 'conf 0.85 → STRONG', tag: 'eligible ★ winner' },
      { m: 'M-036 “White & Williams…”', s: 'conf 0.70', tag: 'eligible' },
      { m: '46 other open matters', s: '—', tag: 'below threshold' },
    ],
    idf: 'IDF for M-020:  max 4.96 · sum 14.33 · nDist 3  → distinctive',
    verdict: 'argmax( conf , idf.sum ) → FILE into M-020    (2 eligible of 48)',
  },
  idfDetail: {
    title: 'C · TIE-BREAK — Distinctiveness (IDF), runs only when confidence is middling',
    steps: [
      '{{key_entities_in_issue}}',
      'df(entity) = # candidate matters that contain it   (the issue’s own matter excluded)',
      'idf = log( N / (1 + df) )     N = # open candidate matters',
      'roll up →  idf.max · idf.sum · idf.ndist (entities with idf ≥ 1.5)',
      'distinctive?  idf.max ≥ 3.0   OR   ndist ≥ 2   OR   idf.sum ≥ 4.0',
    ],
    tagline: 'A rare shared name dominates; shared officers / systems ≈ 0.',
    outcomes: [
      { cond: 'distinctive', result: 'candidate becomes eligible → argmax may pick it → FILE into Existing' },
      { cond: 'not distinctive', result: 'candidate stays ineligible → if none qualify → create NEW' },
    ],
  },
}

export const SUBSTANTIVE_STORIES: SubstantiveStory[] = [FILING_STORY]
